import { useState, useEffect, useCallback } from 'react';
import { Modal, Form } from '../../';
import {
    getSourcesWithCACreds,
    getMigrationTypeFlags,
    getShowUseStoredInfoCheckbox,
    validateCredentialSets,
    prepareMigrationData,
} from '../utils/migrationDialogUtils';

/**
 * Custom hook for Migration Readiness form state and logic
 */
export const useMigrationReadinessForm = ({ open, selectedSources, onConfirm }) => {
    const [form] = Form.useForm();
    const [selectedAccountId, setSelectedAccountId] = useState(null);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [migrationType, setMigrationType] = useState('full');
    const [credentialSets, setCredentialSets] = useState({});
    const [showCredentialSelector, setShowCredentialSelector] = useState(false);
    const [loading, setLoading] = useState(false);

    // Derived state
    const selectedSourcesWithCACreds = getSourcesWithCACreds(selectedSources);
    const migrationTypeFlags = getMigrationTypeFlags(selectedSources);
    const showUseStoredInfoCheckbox = getShowUseStoredInfoCheckbox(selectedSources);

    // Handle target account change
    const handleTargetAccountChange = useCallback(
        (accountId, processedCloudAccounts) => {
            const account = processedCloudAccounts.find((acc) => acc.id === accountId);
            setSelectedAccountId(accountId);
            setSelectedAccount(account);

            // Show credential selector if there are sources with CA credentials
            setShowCredentialSelector(selectedSourcesWithCACreds.length > 0);

            // Reset credential sets when account changes
            setCredentialSets({});
        },
        [selectedSourcesWithCACreds.length],
    );

    // Handle migration type change
    const handleMigrationTypeChange = useCallback((value) => {
        setMigrationType(value);
    }, []);

    // Handle credential set changes
    const handleCredentialSetChange = useCallback((sourceId, credentialSetTag) => {
        setCredentialSets((prev) => ({
            ...prev,
            [sourceId]: credentialSetTag,
        }));
    }, []);

    // Handle form confirmation
    const handleConfirm = useCallback(async () => {
        try {
            setLoading(true);

            // Validate form
            const formValues = await form.validateFields();

            // Validate credential sets if needed
            if (selectedSourcesWithCACreds.length > 0) {
                const hasUnsetCredentials = validateCredentialSets(selectedSourcesWithCACreds, credentialSets);
                if (hasUnsetCredentials) {
                    Modal.error({
                        title: 'Missing Credential Sets',
                        content: 'Please select credential sets for all sources that require them.',
                    });
                    return;
                }
            }

            // Prepare migration data
            const migrationData = prepareMigrationData(formValues, selectedAccount, credentialSets, selectedSources);

            // Call the confirmation handler
            onConfirm(migrationData);
        } catch (error) {
            console.error('Form validation failed:', error);
        } finally {
            setLoading(false);
        }
    }, [form, selectedAccount, credentialSets, selectedSources, selectedSourcesWithCACreds, onConfirm]);

    // Reset form when dialog opens/closes
    useEffect(() => {
        if (open) {
            form.resetFields();
            setSelectedAccountId(null);
            setSelectedAccount(null);
            setMigrationType('full');
            setCredentialSets({});
            setShowCredentialSelector(false);
            setLoading(false);
        }
    }, [open, form]);

    return {
        // Form state
        form,
        selectedAccountId,
        selectedAccount,
        migrationType,
        credentialSets,
        showCredentialSelector,
        loading,

        // Derived state
        selectedSourcesWithCACreds,
        migrationTypeFlags,
        showUseStoredInfoCheckbox,

        // Handlers
        handleTargetAccountChange,
        handleMigrationTypeChange,
        handleCredentialSetChange,
        handleConfirm,
    };
};
